import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { markdownToSpeech } from '../utils/markdownToSpeech';

interface SpeechSynthesisContextValue {
  supported: boolean;
  speaking: boolean;
  /** Speak markdown text (stripped to plain). Cancels any prior utterance. */
  speak: (markdown: string, opts?: { onStart?: () => void; onBlocked?: () => void }) => void;
  stop: () => void;
  /** Fire a silent warm-up inside a user gesture to keep iOS speech authorized. */
  unlock: () => void;
}

const Ctx = createContext<SpeechSynthesisContextValue | undefined>(undefined);

export const SpeechSynthesisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
  const blockedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!supported) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      voiceRef.current = voices.find((v) => v.lang === 'en-US') || voices[0] || null;
    };
    pick();
    window.speechSynthesis.addEventListener?.('voiceschanged', pick);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', pick);
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    if (blockedTimerRef.current) { clearTimeout(blockedTimerRef.current); blockedTimerRef.current = null; }
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback<SpeechSynthesisContextValue['speak']>((markdown, opts) => {
    if (!supported) { opts?.onBlocked?.(); return; }
    window.speechSynthesis.cancel(); // single voice at a time (Codex #9)
    const text = markdownToSpeech(markdown);
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    if (voiceRef.current) u.voice = voiceRef.current;
    u.lang = 'en-US';
    let started = false;
    u.onstart = () => {
      started = true;
      if (blockedTimerRef.current) { clearTimeout(blockedTimerRef.current); blockedTimerRef.current = null; }
      setSpeaking(true);
      opts?.onStart?.();
    };
    u.onend = () => {
      if (blockedTimerRef.current) { clearTimeout(blockedTimerRef.current); blockedTimerRef.current = null; }
      setSpeaking(false);
    };
    u.onerror = () => {
      if (blockedTimerRef.current) { clearTimeout(blockedTimerRef.current); blockedTimerRef.current = null; }
      setSpeaking(false);
    };
    window.speechSynthesis.speak(u);
    // iOS can fail silently — if onstart never fires, surface "tap to play".
    if (blockedTimerRef.current) clearTimeout(blockedTimerRef.current);
    blockedTimerRef.current = setTimeout(() => {
      blockedTimerRef.current = null;
      if (!started) opts?.onBlocked?.();
    }, 1200);
  }, [supported]);

  const unlock = useCallback(() => {
    // BEST-EFFORT iOS warm-up (Codex P2-b). A zero-volume utterance inside a
    // user gesture *sometimes* keeps Safari's speech queue authorized, but it
    // is NOT a reliable unlock — which is exactly why auto-read is documented
    // best-effort (spec §6.3) and always backed by the guaranteed per-answer
    // play button + the "tap to play" fallback. Do not treat this as a promise.
    if (!supported) return;
    try {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      window.speechSynthesis.speak(u);
    } catch { /* best-effort */ }
  }, [supported]);

  return (
    <Ctx.Provider value={{ supported, speaking, speak, stop, unlock }}>
      {children}
    </Ctx.Provider>
  );
};

export function useSpeechSynthesis(): SpeechSynthesisContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Allow use outside the provider during incremental wiring — no-op.
    return { supported: false, speaking: false, speak: () => {}, stop: () => {}, unlock: () => {} };
  }
  return ctx;
}
